/**
 * PTG reference slice — unified workbench routes that inherit sellable polish.
 * @see .cursor/plans/professional-tool-grammar/OVERVIEW.md §5
 */

/** WB4.3-S1 Inc 5a — consumable joins PTG item reference slice. */
export const PTG_REFERENCE_ITEM_SUBDOMAINS = [
  'sellable',
  'service',
  'asset',
  'consumable',
] as const;
export const PTG_REFERENCE_ENTITY_SUBDOMAINS = ['internal', 'external', 'user'] as const;
/** WB4.2 Inc 4 — manufacturing unified runtime (WB4.3-S1 Inc 3: full subdomain cluster). */
export const ORDER_PILOT_DOMAIN = 'manufacturing' as const;
export const ORDER_PILOT_SUBDOMAINS = ['bom', 'production-order', 'quality-inspection'] as const;
/** WB4.2 Inc 5 + WB4.3-S1 Inc 5 — finance sub-resource unified runtime (state · flow · ops). */
export const FINANCE_REFERENCE_DOMAIN = 'finance' as const;
export const FINANCE_REFERENCE_SUBDOMAINS = ['state', 'flow'] as const;
/** WB4.3-S1 Inc 5 — finance ops unified runtime. */
export const FINANCE_OPS_SUBDOMAINS = ['budget', 'vouchers', 'receivables', 'overdue'] as const;
/** Scanned finance ops route outside subdomain list/detail pattern. */
export const FINANCE_OPS_RUNTIME_SCANNED_ROUTES = ['/finance/voucher/:streamType/new'] as const;
/** WB4.3-S1 Inc 6 — former layout_pilot scanned routes (matrix maturity → runtime). */
export const LAYOUT_PILOT_RUNTIME_PROMOTED_SCANNED_ROUTES = [
  '/entity/external/create',
  '/flow/assignments/:id',
  '/flow/executions/:id',
  '/flow/streams',
  '/flow/streams/:id/execute',
  '/workspace/operations/:id',
  '/workspace/tasks/:taskExecutionId',
] as const;
/** WB4.3-S1 — appeal marketing hub list (shell when appeal flag on). */
export const APPEAL_CONTENT_RUNTIME_SCANNED_ROUTES = ['/appeal/content'] as const;
/** WB4.3 Batch D1 — utility hub pages promoted to BusinessWorkbenchShell. */
export const UTILITY_RUNTIME_SCANNED_ROUTES = [
  '/approvals/pending',
  '/approvals/history',
  '/automation',
  '/export',
  '/appeal/campaign',
] as const;
/** WB4.3 Batch D2 — system admin list pages promoted to BusinessWorkbenchShell. */
export const SYSTEM_ADMIN_RUNTIME_SCANNED_ROUTES = [
  '/system/audit',
  '/system/site-join-applications',
  '/system/site-readiness',
] as const;
/** WB4.3 Batch D2 — schema wizard shell gate (layout_pilot maturity). */
export const SYSTEM_SCHEMA_CREATE_LAYOUT_PILOT_ROUTE = '/system/schema/create' as const;
/**
 * WB4.3 Batch E3 — platform / tenant / workspace settings scanned into business matrix.
 * Intentional exit bucket (`platform_admin`); business workbench shell inappropriate.
 */
export const PLATFORM_ADMIN_INTENTIONAL_LEGACY_ROUTE_PREFIXES = [
  '/settings/',
  '/system/settings',
] as const;
export const PLATFORM_ADMIN_INTENTIONAL_LEGACY_EXACT_ROUTES = ['/workspace/settings'] as const;
/** @deprecated Use PLATFORM_ADMIN_INTENTIONAL_LEGACY_ROUTE_PREFIXES — kept for grep/SSOT continuity */
export const PLATFORM_PLANE_LEGACY_OPS_ROUTES = ['/settings/billing/:tenantId'] as const;
/** WB4.3 Batch E1 — flow hub / builder pages (no list/detail shell pattern; TL-8 defer). */
export const FLOW_UTILITY_LEGACY_ROUTES = [
  '/flow/dispatch',
  '/flow/history',
  '/flow/new',
  '/flow/definitions/new',
  '/flow/definitions/basic',
] as const;
/** WB4.3 Batch E1 — custom board UX (assignment board; TL-8 defer). */
export const CUSTOM_SURFACE_LEGACY_ROUTES = ['/workspace/assignments'] as const;
/** WB4.3 Batch E2 — conscious hub + panels; ConsciousLayout, not BusinessWorkbenchShell. */
export const CONSCIOUS_PLANE_LEGACY_OPS_ROUTES = [
  '/conscious',
  '/conscious/alerts',
  '/conscious/suggestions',
  '/conscious/ask',
  '/conscious/wisdom',
  '/conscious/stream',
  '/conscious/memory',
  '/conscious/learning',
  '/conscious/pattern',
  '/conscious/neuro',
  '/conscious/org-state',
  '/conscious/brain',
] as const;
export const FINANCE_UNIFIED_RUNTIME_SUBDOMAINS = [
  ...FINANCE_REFERENCE_SUBDOMAINS,
  ...FINANCE_OPS_SUBDOMAINS,
] as const;
/** WB4.2 Inc 5 — finance-advanced sub-resource unified runtime. */
export const FINANCE_ADVANCED_REFERENCE_DOMAIN = 'finance-advanced' as const;
export const FINANCE_ADVANCED_REFERENCE_SUBDOMAINS = [
  'legal-entity',
  'currency-rate',
  'intercompany',
] as const;
/** WB4.2 — knowledge document subdomains (report · policy · plan). */
export const KNOWLEDGE_PILOT_DOMAIN = 'knowledge' as const;
export const KNOWLEDGE_PILOT_SUBDOMAINS = ['report', 'policy', 'plan'] as const;
/** WB4.3-S1 Inc 4b — space sub-resource unified runtime (physical · virtual). */
export const SPACE_PILOT_DOMAIN = 'space' as const;
export const SPACE_PILOT_SUBDOMAINS = ['physical', 'virtual'] as const;
/** WB4.3-S1 Inc 1 — appeal sub-resource unified runtime (flag opt-in). */
export const APPEAL_REFERENCE_DOMAIN = 'appeal' as const;
export const APPEAL_REFERENCE_SUBDOMAINS = [
  'content',
  'semantic-formation',
  'ephemeral-formation',
  'ephemeral-performance',
  'recurrent-performance',
] as const;
/** WB4.3-S1 Inc 2 — calendar sub-resource unified runtime. */
export const CALENDAR_REFERENCE_DOMAIN = 'calendar' as const;
export const CALENDAR_REFERENCE_SUBDOMAINS = ['event', 'trigger', 'cycle'] as const;
/** WB4.3-S1 Inc 3 — event sub-resource unified runtime (L0 definition · execution). */
export const EVENT_REFERENCE_DOMAIN = 'event' as const;
export const EVENT_REFERENCE_SUBDOMAINS = [
  'flow-definition',
  'stream-definition',
  'action-binding',
  'flow-execution',
  'stream-execution',
  'flow-event',
  'stream-event',
] as const;
/** WB4.3-S1 Inc 3 — log sub-resource unified runtime. */
export const LOG_REFERENCE_DOMAIN = 'log' as const;
export const LOG_REFERENCE_SUBDOMAINS = ['flow', 'stream'] as const;
/** WB4.3-S1 Inc 5b — warehouse sub-resource unified runtime. */
export const WAREHOUSE_PILOT_DOMAIN = 'warehouse' as const;
export const WAREHOUSE_PILOT_SUBDOMAINS = ['picking-list', 'receiving'] as const;
/** WB4.3-S1 Inc 5b — inventory sub-resource unified runtime. */
export const INVENTORY_PILOT_DOMAIN = 'inventory' as const;
export const INVENTORY_PILOT_SUBDOMAINS = ['shortage'] as const;
/** WB4.3-S1 Inc 6 — flow stream list unified runtime (`/flow/streams`). */
export const FLOW_PILOT_DOMAIN = 'flow' as const;
export const FLOW_PILOT_SUBDOMAINS = ['streams', 'assignments'] as const;

export type PtgReferenceItemSubdomain = (typeof PTG_REFERENCE_ITEM_SUBDOMAINS)[number];
export type PtgReferenceEntitySubdomain = (typeof PTG_REFERENCE_ENTITY_SUBDOMAINS)[number];
export type OrderPilotSubdomain = (typeof ORDER_PILOT_SUBDOMAINS)[number];
export type FinanceReferenceSubdomain = (typeof FINANCE_REFERENCE_SUBDOMAINS)[number];
export type FinanceOpsSubdomain = (typeof FINANCE_OPS_SUBDOMAINS)[number];
export type FinanceAdvancedReferenceSubdomain =
  (typeof FINANCE_ADVANCED_REFERENCE_SUBDOMAINS)[number];
export type KnowledgePilotSubdomain = (typeof KNOWLEDGE_PILOT_SUBDOMAINS)[number];
export type SpacePilotSubdomain = (typeof SPACE_PILOT_SUBDOMAINS)[number];
export type AppealReferenceSubdomain = (typeof APPEAL_REFERENCE_SUBDOMAINS)[number];
export type CalendarReferenceSubdomain = (typeof CALENDAR_REFERENCE_SUBDOMAINS)[number];
export type EventReferenceSubdomain = (typeof EVENT_REFERENCE_SUBDOMAINS)[number];
export type LogReferenceSubdomain = (typeof LOG_REFERENCE_SUBDOMAINS)[number];
export type WarehousePilotSubdomain = (typeof WAREHOUSE_PILOT_SUBDOMAINS)[number];
export type InventoryPilotSubdomain = (typeof INVENTORY_PILOT_SUBDOMAINS)[number];
export type FlowPilotSubdomain = (typeof FLOW_PILOT_SUBDOMAINS)[number];

function normalizeRouteSegment(value: string | undefined): string | undefined {
  const trimmed = value?.trim().toLowerCase();
  return trimmed && trimmed.length > 0 ? trimmed : undefined;
}

export function isPtgReferenceItemSubdomain(
  subdomain: string | undefined
): subdomain is PtgReferenceItemSubdomain {
  return PTG_REFERENCE_ITEM_SUBDOMAINS.includes(subdomain as PtgReferenceItemSubdomain);
}

export function isPtgReferenceEntitySubdomain(
  subdomain: string | undefined
): subdomain is PtgReferenceEntitySubdomain {
  return PTG_REFERENCE_ENTITY_SUBDOMAINS.includes(subdomain as PtgReferenceEntitySubdomain);
}

export function isOrderPilotSubdomain(
  subdomain: string | undefined
): subdomain is OrderPilotSubdomain {
  return ORDER_PILOT_SUBDOMAINS.includes(subdomain as OrderPilotSubdomain);
}

export function isKnowledgePilotSubdomain(
  subdomain: string | undefined
): subdomain is KnowledgePilotSubdomain {
  return KNOWLEDGE_PILOT_SUBDOMAINS.includes(subdomain as KnowledgePilotSubdomain);
}

export function isSpacePilotSubdomain(
  subdomain: string | undefined
): subdomain is SpacePilotSubdomain {
  return SPACE_PILOT_SUBDOMAINS.includes(subdomain as SpacePilotSubdomain);
}

export function isAppealReferenceSubdomain(
  subdomain: string | undefined
): subdomain is AppealReferenceSubdomain {
  return APPEAL_REFERENCE_SUBDOMAINS.includes(subdomain as AppealReferenceSubdomain);
}

export function isCalendarReferenceSubdomain(
  subdomain: string | undefined
): subdomain is CalendarReferenceSubdomain {
  return CALENDAR_REFERENCE_SUBDOMAINS.includes(subdomain as CalendarReferenceSubdomain);
}

export function isEventReferenceSubdomain(
  subdomain: string | undefined
): subdomain is EventReferenceSubdomain {
  return EVENT_REFERENCE_SUBDOMAINS.includes(subdomain as EventReferenceSubdomain);
}

export function isLogReferenceSubdomain(
  subdomain: string | undefined
): subdomain is LogReferenceSubdomain {
  return LOG_REFERENCE_SUBDOMAINS.includes(subdomain as LogReferenceSubdomain);
}

export function isWarehousePilotSubdomain(
  subdomain: string | undefined
): subdomain is WarehousePilotSubdomain {
  return WAREHOUSE_PILOT_SUBDOMAINS.includes(subdomain as WarehousePilotSubdomain);
}

export function isInventoryPilotSubdomain(
  subdomain: string | undefined
): subdomain is InventoryPilotSubdomain {
  return INVENTORY_PILOT_SUBDOMAINS.includes(subdomain as InventoryPilotSubdomain);
}

export function isFlowPilotSubdomain(
  subdomain: string | undefined
): subdomain is FlowPilotSubdomain {
  return FLOW_PILOT_SUBDOMAINS.includes(subdomain as FlowPilotSubdomain);
}

export function isOrderPilotWorkbenchScope(domain?: string, subdomain?: string): boolean {
  return (
    normalizeRouteSegment(domain) === ORDER_PILOT_DOMAIN &&
    isOrderPilotSubdomain(normalizeRouteSegment(subdomain))
  );
}

export function isFinanceReferenceSubdomain(
  subdomain: string | undefined
): subdomain is FinanceReferenceSubdomain {
  return FINANCE_REFERENCE_SUBDOMAINS.includes(subdomain as FinanceReferenceSubdomain);
}

export function isFinanceOpsSubdomain(
  subdomain: string | undefined
): subdomain is FinanceOpsSubdomain {
  return FINANCE_OPS_SUBDOMAINS.includes(subdomain as FinanceOpsSubdomain);
}

export function isFinanceAdvancedReferenceSubdomain(
  subdomain: string | undefined
): subdomain is FinanceAdvancedReferenceSubdomain {
  return FINANCE_ADVANCED_REFERENCE_SUBDOMAINS.includes(
    subdomain as FinanceAdvancedReferenceSubdomain
  );
}

export function isFinanceReferenceWorkbenchScope(domain?: string, subdomain?: string): boolean {
  return (
    normalizeRouteSegment(domain) === FINANCE_REFERENCE_DOMAIN &&
    isFinanceReferenceSubdomain(normalizeRouteSegment(subdomain))
  );
}

export function isFinanceOpsWorkbenchScope(domain?: string, subdomain?: string): boolean {
  return (
    normalizeRouteSegment(domain) === FINANCE_REFERENCE_DOMAIN &&
    isFinanceOpsSubdomain(normalizeRouteSegment(subdomain))
  );
}

export function isFinanceAdvancedReferenceWorkbenchScope(
  domain?: string,
  subdomain?: string
): boolean {
  return (
    normalizeRouteSegment(domain) === FINANCE_ADVANCED_REFERENCE_DOMAIN &&
    isFinanceAdvancedReferenceSubdomain(normalizeRouteSegment(subdomain))
  );
}

export function isKnowledgePilotWorkbenchScope(domain?: string, subdomain?: string): boolean {
  return (
    normalizeRouteSegment(domain) === KNOWLEDGE_PILOT_DOMAIN &&
    isKnowledgePilotSubdomain(normalizeRouteSegment(subdomain))
  );
}

export function isSpacePilotWorkbenchScope(domain?: string, subdomain?: string): boolean {
  return (
    normalizeRouteSegment(domain) === SPACE_PILOT_DOMAIN &&
    isSpacePilotSubdomain(normalizeRouteSegment(subdomain))
  );
}

export function isAppealReferenceWorkbenchScope(domain?: string, subdomain?: string): boolean {
  return (
    normalizeRouteSegment(domain) === APPEAL_REFERENCE_DOMAIN &&
    isAppealReferenceSubdomain(normalizeRouteSegment(subdomain))
  );
}

export function isCalendarReferenceWorkbenchScope(domain?: string, subdomain?: string): boolean {
  return (
    normalizeRouteSegment(domain) === CALENDAR_REFERENCE_DOMAIN &&
    isCalendarReferenceSubdomain(normalizeRouteSegment(subdomain))
  );
}

export function isEventReferenceWorkbenchScope(domain?: string, subdomain?: string): boolean {
  return (
    normalizeRouteSegment(domain) === EVENT_REFERENCE_DOMAIN &&
    isEventReferenceSubdomain(normalizeRouteSegment(subdomain))
  );
}

export function isLogReferenceWorkbenchScope(domain?: string, subdomain?: string): boolean {
  return (
    normalizeRouteSegment(domain) === LOG_REFERENCE_DOMAIN &&
    isLogReferenceSubdomain(normalizeRouteSegment(subdomain))
  );
}

export function isWarehousePilotWorkbenchScope(domain?: string, subdomain?: string): boolean {
  return (
    normalizeRouteSegment(domain) === WAREHOUSE_PILOT_DOMAIN &&
    isWarehousePilotSubdomain(normalizeRouteSegment(subdomain))
  );
}

export function isInventoryPilotWorkbenchScope(domain?: string, subdomain?: string): boolean {
  return (
    normalizeRouteSegment(domain) === INVENTORY_PILOT_DOMAIN &&
    isInventoryPilotSubdomain(normalizeRouteSegment(subdomain))
  );
}

export function isFlowPilotWorkbenchScope(domain?: string, subdomain?: string): boolean {
  return (
    normalizeRouteSegment(domain) === FLOW_PILOT_DOMAIN &&
    isFlowPilotSubdomain(normalizeRouteSegment(subdomain))
  );
}

/**
 * WB4.4 P1 + Condition ① — subdomains that inherit sellable PTG list polish.
 * Customer proxy: `entity/external` · Order proxy: `manufacturing/production-order`.
 * Supplier proxy: `entity/external` · Collection proxy: `finance/receivables`.
 */
export const PTG_REFERENCE_UNIFIED_LIST_SUBDOMAINS = [
  ...PTG_REFERENCE_ITEM_SUBDOMAINS,
  ...PTG_REFERENCE_ENTITY_SUBDOMAINS,
  ...FINANCE_UNIFIED_RUNTIME_SUBDOMAINS,
  ...SPACE_PILOT_SUBDOMAINS,
  ...KNOWLEDGE_PILOT_SUBDOMAINS,
  ...INVENTORY_PILOT_SUBDOMAINS,
  ...ORDER_PILOT_SUBDOMAINS,
  ...WAREHOUSE_PILOT_SUBDOMAINS,
  ...APPEAL_REFERENCE_SUBDOMAINS,
  ...CALENDAR_REFERENCE_SUBDOMAINS,
  ...EVENT_REFERENCE_SUBDOMAINS,
  ...LOG_REFERENCE_SUBDOMAINS,
  ...FLOW_PILOT_SUBDOMAINS,
] as const;

export type PtgReferenceUnifiedListSubdomain =
  (typeof PTG_REFERENCE_UNIFIED_LIST_SUBDOMAINS)[number];

/** List surface PTG polish (progressive actions · inline edit · context menu focus). */
export function isPtgReferenceUnifiedListSubdomain(
  subdomain: string | undefined
): subdomain is PtgReferenceUnifiedListSubdomain {
  return PTG_REFERENCE_UNIFIED_LIST_SUBDOMAINS.includes(
    subdomain as PtgReferenceUnifiedListSubdomain
  );
}

/** Auxiliary real-data scope for PTG reference workbench routes. */
export function isPtgReferenceWorkbenchScope(domain?: string, subdomain?: string): boolean {
  const normalizedDomain = normalizeRouteSegment(domain);
  const normalizedSubdomain = normalizeRouteSegment(subdomain);

  if (!normalizedDomain && !normalizedSubdomain) {
    return true;
  }
  if (normalizedDomain === 'item' && isPtgReferenceItemSubdomain(normalizedSubdomain)) {
    return true;
  }
  if (normalizedDomain === 'entity' && isPtgReferenceEntitySubdomain(normalizedSubdomain)) {
    return true;
  }
  if (isOrderPilotWorkbenchScope(normalizedDomain, normalizedSubdomain)) {
    return true;
  }
  if (isKnowledgePilotWorkbenchScope(normalizedDomain, normalizedSubdomain)) {
    return true;
  }
  if (isSpacePilotWorkbenchScope(normalizedDomain, normalizedSubdomain)) {
    return true;
  }
  if (isFinanceReferenceWorkbenchScope(normalizedDomain, normalizedSubdomain)) {
    return true;
  }
  if (isFinanceOpsWorkbenchScope(normalizedDomain, normalizedSubdomain)) {
    return true;
  }
  if (isFinanceAdvancedReferenceWorkbenchScope(normalizedDomain, normalizedSubdomain)) {
    return true;
  }
  if (isAppealReferenceWorkbenchScope(normalizedDomain, normalizedSubdomain)) {
    return true;
  }
  if (isCalendarReferenceWorkbenchScope(normalizedDomain, normalizedSubdomain)) {
    return true;
  }
  if (isEventReferenceWorkbenchScope(normalizedDomain, normalizedSubdomain)) {
    return true;
  }
  if (isLogReferenceWorkbenchScope(normalizedDomain, normalizedSubdomain)) {
    return true;
  }
  if (isWarehousePilotWorkbenchScope(normalizedDomain, normalizedSubdomain)) {
    return true;
  }
  if (isInventoryPilotWorkbenchScope(normalizedDomain, normalizedSubdomain)) {
    return true;
  }
  if (isFlowPilotWorkbenchScope(normalizedDomain, normalizedSubdomain)) {
    return true;
  }
  return false;
}

const LAYOUT_PILOT_RUNTIME_PROMOTED_SCANNED_ROUTE_SET = new Set<string>(
  LAYOUT_PILOT_RUNTIME_PROMOTED_SCANNED_ROUTES
);

/** Matrix URL pattern match for Inc 6 layout_pilot → runtime scanned routes. */
export function isLayoutPilotRuntimePromotedScannedRoute(url: string): boolean {
  if (LAYOUT_PILOT_RUNTIME_PROMOTED_SCANNED_ROUTE_SET.has(url)) {
    return true;
  }
  const normalized = (url.split('?')[0] ?? url).replace(/\/$/, '');
  if (normalized === '/entity/external/create') {
    return true;
  }
  if (/^\/flow\/assignments\/[^/]+$/.test(normalized)) {
    return true;
  }
  if (/^\/flow\/executions\/[^/]+$/.test(normalized)) {
    return true;
  }
  if (/^\/workspace\/tasks\/[^/]+$/.test(normalized)) {
    return true;
  }
  if (normalized === '/flow/streams') {
    return true;
  }
  if (/^\/flow\/streams\/[^/]+\/execute$/.test(normalized)) {
    return true;
  }
  if (/^\/workspace\/operations\/[^/]+$/.test(normalized)) {
    return true;
  }
  if (normalized === '/appeal/content') {
    return true;
  }
  return false;
}

export function isUtilityRuntimeScannedRoute(url: string): boolean {
  return (UTILITY_RUNTIME_SCANNED_ROUTES as readonly string[]).includes(url);
}

export function isSystemAdminRuntimeScannedRoute(url: string): boolean {
  return (SYSTEM_ADMIN_RUNTIME_SCANNED_ROUTES as readonly string[]).includes(url);
}

export function isSystemSchemaCreateLayoutPilotRoute(url: string): boolean {
  return url === SYSTEM_SCHEMA_CREATE_LAYOUT_PILOT_ROUTE;
}

export function isAppealContentRuntimeScannedRoute(url: string): boolean {
  return (APPEAL_CONTENT_RUNTIME_SCANNED_ROUTES as readonly string[]).includes(url);
}

export function isPlatformAdminIntentionalLegacyRoute(url: string): boolean {
  const normalized = url.trim();
  if (!normalized || normalized === '—') return false;
  if ((PLATFORM_ADMIN_INTENTIONAL_LEGACY_EXACT_ROUTES as readonly string[]).includes(normalized)) {
    return true;
  }
  return (PLATFORM_ADMIN_INTENTIONAL_LEGACY_ROUTE_PREFIXES as readonly string[]).some((prefix) =>
    normalized.startsWith(prefix)
  );
}

/** @deprecated Use isPlatformAdminIntentionalLegacyRoute */
export function isPlatformPlaneLegacyOpsRoute(url: string): boolean {
  return isPlatformAdminIntentionalLegacyRoute(url);
}

export function isFlowUtilityLegacyRoute(url: string): boolean {
  return (FLOW_UTILITY_LEGACY_ROUTES as readonly string[]).includes(url);
}

export function isCustomSurfaceLegacyRoute(url: string): boolean {
  return (CUSTOM_SURFACE_LEGACY_ROUTES as readonly string[]).includes(url);
}

export function isConsciousPlaneLegacyOpsRoute(url: string): boolean {
  return (CONSCIOUS_PLANE_LEGACY_OPS_ROUTES as readonly string[]).includes(url);
}
